import type { Query } from "@malloydata/malloy-interfaces";
import type {
  QueryDefinition,
  QueryResult,
  ResultField,
  VisualizationConfig,
  WidgetWidth,
  Cell,
  Row,
} from "./types";
import { reportText } from "./guided-query";
import { MODEL_VERSION } from "./demo-data";
import { tx, type Locale } from "./i18n";

export type InputType =
  "number" | "string" | "boolean" | "date" | "timestamp" | "array" | "record";
export interface WidgetInput {
  name: string;
  type: InputType;
  nullable: boolean;
}
export interface WidgetDefinition {
  id: string;
  name: string;
  description: string;
  revision: number;
  builtin?: boolean;
  inputs: WidgetInput[];
  visualization: VisualizationConfig;
  renderer?: "billing-matrix";
  localizedInputs?: string[];
}
export interface ParameterDefinition {
  name: string;
  label: string;
  type: "date" | "string" | "number" | "boolean";
  default: string | number | boolean;
}
export type Parameters = Record<string, string | number | boolean>;
export type BindingQuery =
  | { mode: "guided"; query: Query }
  | { mode: "malloy"; malloy: string; guidedDraft?: Query };
export interface DataBindingDefinition {
  id: string;
  name: string;
  revision: number;
  query: BindingQuery;
  parameters: ParameterDefinition[];
  inputMapping: Record<string, string>;
  builtin?: boolean;
}
export interface WidgetPlacement {
  id: string;
  widgetId: string;
  bindingId: string;
  width: WidgetWidth;
  title?: string;
  description?: string;
  visualization?: Partial<VisualizationConfig>;
  inputMapping?: Record<string, string>;
  parameters?: Parameters;
}
export interface ReportingDashboard {
  id: string;
  name: string;
  description: string;
  builtin?: boolean;
  widgets: WidgetPlacement[];
  parameters: ParameterDefinition[];
  sourceAt: string;
}
export type ConditionOperator =
  | "hasRows"
  | "noRows"
  | "eq"
  | "ne"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "isNull"
  | "notNull";
export interface SectionCondition {
  bindingId: string;
  operator: ConditionOperator;
  field?: string;
  value?: string | number | boolean;
}
export type TemplateSection = {
  id: string;
  title: string;
  condition?: SectionCondition;
  columns?: 1 | 2;
} & (
  | { kind: "text"; text: string }
  | { kind: "widget"; widgetId: string; bindingId: string }
);
export interface ReportTemplateDefinition {
  id: string;
  name: string;
  description: string;
  revision: number;
  builtin?: boolean;
  parameters: ParameterDefinition[];
  bindingIds: string[];
  sections: TemplateSection[];
  sourceAt: string;
  propertyName?: string;
}
export interface ReportingState {
  version: 3;
  widgets: WidgetDefinition[];
  bindings: DataBindingDefinition[];
  templates: ReportTemplateDefinition[];
  dashboards: ReportingDashboard[];
  deletedBuiltins: string[];
}
export interface ResolvedSection {
  id: string;
  title: string;
  text?: string;
  columns?: 1 | 2;
  widget?: WidgetDefinition;
  data?: QueryResult;
}
export interface GeneratedReport {
  id: string;
  name: string;
  templateId: string;
  templateRevision: number;
  propertyName?: string;
  generatedAt: string;
  sourceAt: string;
  locale: Locale;
  parameters: Parameters;
  sections: ResolvedSection[];
  bindingResults: Record<string, QueryResult>;
  definitions: {
    template: ReportTemplateDefinition;
    widgets: WidgetDefinition[];
    bindings: DataBindingDefinition[];
  };
}
export const defaultsFor = (definitions: ParameterDefinition[]): Parameters =>
  Object.fromEntries(definitions.map((p) => [p.name, p.default]));
export function validateParameters(
  definitions: ParameterDefinition[],
  values: Parameters,
): Parameters {
  const result = { ...defaultsFor(definitions), ...values };
  for (const p of definitions) {
    const value = result[p.name];
    if (p.type === "date") {
      if (
        typeof value !== "string" ||
        !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
        new Date(value).toISOString().slice(0, 10) !== value
      )
        throw new Error(`Invalid date: ${p.label}`);
    } else if (
      typeof value !== p.type ||
      (p.type === "number" && !Number.isFinite(value))
    )
      throw new Error(`Invalid parameter: ${p.label}`);
  }
  if (result.from && result.to && result.from > result.to)
    throw new Error("The start date must be before the end date.");
  if (
    typeof result.from === "string" &&
    typeof result.to === "string" &&
    (Date.parse(result.to) - Date.parse(result.from)) / 86400000 > 366
  )
    throw new Error("Select a reporting period of at most 366 days.");
  return result;
}
export function bindingEditor(
  binding: DataBindingDefinition,
  widget?: WidgetDefinition,
): QueryDefinition {
  return {
    ...binding.query,
    id: binding.id,
    name: binding.name,
    description: "Data binding",
    modelVersion: MODEL_VERSION,
    tags: [],
    author: { id: "manager", name: "Luuta Operations Manager" },
    visualization: widget?.visualization ?? {
      type: "table",
      x: "",
      y: [],
      format: "number",
    },
  };
}
export function bindingText(
  binding: DataBindingDefinition,
  parameters: Parameters = {},
): string {
  const values = validateParameters(binding.parameters, parameters);
  return reportText(bindingEditor(binding)).replace(
    /\{\{([\w]+)\}\}/g,
    (_, name: string) => {
      const spec = binding.parameters.find((p) => p.name === name);
      if (!spec) throw new Error(`Unknown binding parameter: ${name}`);
      const value = values[name];
      return spec.type === "date"
        ? `@${value}`
        : spec.type === "string"
          ? JSON.stringify(value)
          : String(value);
    },
  );
}
export function inputSignature(data: QueryResult): WidgetInput[] {
  return data.fields.map((f) => ({
    name: f.name,
    type: f.type as InputType,
    nullable: data.rows.some((r) => r[f.name] === null),
  }));
}
export function mapWidgetInputs(
  widget: WidgetDefinition,
  result: QueryResult,
  mapping: Record<string, string> = {},
): QueryResult {
  if (result.truncated)
    throw new Error(
      "The result is truncated. Use a bounded or nested query before generating a report.",
    );
  // A typed rows: record[] signature supports generic tables and migrated charts.
  if (
    widget.inputs.length === 1 &&
    widget.inputs[0].name === "rows" &&
    widget.inputs[0].type === "array" &&
    mapping.rows === "$rows"
  )
    return structuredClone(result);
  const fields: ResultField[] = widget.inputs.map((input) => {
    const source = mapping[input.name] || input.name;
    const field = result.fields.find((f) => f.name === source);
    if (!field) throw new Error(`Missing input: ${input.name}`);
    if (field.type !== input.type)
      throw new Error(
        `Input ${input.name} requires ${input.type}; received ${field.type}.`,
      );
    for (const row of result.rows) {
      const value = row[source];
      if ((value === null || value === undefined) && !input.nullable)
        throw new Error(`Input ${input.name} does not allow missing values.`);
      if (
        value != null &&
        (input.type === "number"
          ? typeof value !== "number" || !Number.isFinite(value)
          : input.type === "array"
            ? !Array.isArray(value)
            : input.type === "record"
              ? typeof value !== "object" || Array.isArray(value)
              : ["date", "timestamp"].includes(input.type)
                ? typeof value !== "string" ||
                  !Number.isFinite(Date.parse(value))
                : typeof value !== input.type)
      )
        throw new Error(`Invalid value for input: ${input.name}`);
    }
    return { ...field, name: input.name };
  });
  const rows = result.rows.map(
    (row) =>
      Object.fromEntries(
        widget.inputs.map((i) => [i.name, row[mapping[i.name] || i.name]]),
      ) as Row,
  );
  return { ...result, rows, columns: fields.map((f) => f.name), fields };
}
export function conditionMatches(
  condition: SectionCondition | undefined,
  results: Record<string, QueryResult>,
): boolean {
  if (!condition) return true;
  const data = results[condition.bindingId];
  if (!data || data.truncated)
    throw new Error(
      "A conditional section requires a complete, successful binding result.",
    );
  if (condition.operator === "hasRows") return data.rows.length > 0;
  if (condition.operator === "noRows") return data.rows.length === 0;
  if (!condition.field || !data.columns.includes(condition.field))
    throw new Error("Select a valid condition field.");
  if (data.rows.length !== 1)
    throw new Error("A scalar condition requires exactly one result row.");
  const actual = data.rows[0][condition.field],
    expected = condition.value;
  if (condition.operator === "isNull") return actual === null;
  if (condition.operator === "notNull") return actual !== null;
  if (actual === null) return false;
  if (
    typeof actual !== typeof expected ||
    !["string", "number", "boolean"].includes(typeof actual)
  )
    throw new Error("Condition values must have matching scalar types.");
  if (typeof actual === "boolean" && !["eq", "ne"].includes(condition.operator))
    throw new Error("Boolean conditions support equality comparisons only.");
  switch (condition.operator) {
    case "eq":
      return actual === expected;
    case "ne":
      return actual !== expected;
    case "gt":
      return (actual as string | number) > (expected as string | number);
    case "gte":
      return (actual as string | number) >= (expected as string | number);
    case "lt":
      return (actual as string | number) < (expected as string | number);
    case "lte":
      return (actual as string | number) <= (expected as string | number);
  }
}
export function interpolateText(
  text: string,
  parameters: Parameters,
  results: Record<string, QueryResult>,
  locale?: Locale,
): string {
  return text.replace(
    /\{\{([\w-]+)\.([\w]+)\}\}/g,
    (_, bindingId: string, field: string) => {
      if (bindingId === "params") {
        if (!Object.hasOwn(parameters, field))
          throw new Error(`Unknown text parameter: ${field}`);
        return String(parameters[field]);
      }
      const result = results[bindingId];
      if (
        !result ||
        result.rows.length !== 1 ||
        !result.columns.includes(field)
      )
        throw new Error(
          `Text placeholder requires a single result: ${bindingId}.${field}`,
        );
      const raw = String(result.rows[0][field] ?? "");
      const value =
        result.fields.find((f) => f.name === field)?.type === "date"
          ? raw.slice(0, 10)
          : raw;
      return field === "instruction" && locale ? tx(value, {}, locale) : value;
    },
  );
}
export async function generateReport(
  state: ReportingState,
  templateId: string,
  values: Parameters,
  locale: Locale,
  query: (text: string) => Promise<QueryResult>,
  signal?: AbortSignal,
): Promise<GeneratedReport> {
  // Freeze definitions and scope before the first asynchronous query.
  const frozen = structuredClone(state),
    template = frozen.templates.find((t) => t.id === templateId);
  if (!template) throw new Error("Report Template not found.");
  if (!Number.isFinite(Date.parse(template.sourceAt)))
    throw new Error(
      "Provide a valid source timestamp for the Report Template.",
    );
  const parameters = validateParameters(template.parameters, values);
  const ids = new Set([
    ...template.bindingIds,
    ...template.sections.flatMap((s) => [
      ...(s.kind === "widget" ? [s.bindingId] : []),
      ...(s.condition ? [s.condition.bindingId] : []),
    ]),
  ]);
  const results: Record<string, QueryResult> = {};
  const bindings: DataBindingDefinition[] = [];
  for (const id of ids) {
    signal?.throwIfAborted();
    const binding = frozen.bindings.find((b) => b.id === id);
    if (!binding) throw new Error(`Data binding not found: ${id}`);
    const result = await query(bindingText(binding, parameters));
    if (result.truncated)
      throw new Error(`Incomplete data binding: ${binding.name}`);
    results[id] = structuredClone(result);
    bindings.push(binding);
  }
  signal?.throwIfAborted();
  const widgets: WidgetDefinition[] = [];
  const sections = template.sections.flatMap((section): ResolvedSection[] => {
    if (!conditionMatches(section.condition, results)) return [];
    const title = template.builtin
      ? tx(section.title, {}, locale)
      : section.title;
    if (section.kind === "text")
      return [
        {
          id: section.id,
          title,
          columns: section.columns,
          text: interpolateText(
            template.builtin ? tx(section.text, {}, locale) : section.text,
            parameters,
            results,
            locale,
          ),
        },
      ];
    const widget = frozen.widgets.find((w) => w.id === section.widgetId);
    if (!widget) throw new Error(`Widget not found: ${section.widgetId}`);
    const binding = bindings.find((b) => b.id === section.bindingId)!;
    const data = mapWidgetInputs(
      widget,
      results[binding.id],
      binding.inputMapping,
    );
    for (const row of data.rows)
      for (const name of widget.localizedInputs ?? [])
        if (typeof row[name] === "string")
          row[name] = tx(row[name], {}, locale);
    const freezeLabels = (fields: ResultField[]): ResultField[] =>
      fields.map((f) => ({
        ...f,
        label: tx(f.label, {}, locale),
        ...(f.fields ? { fields: freezeLabels(f.fields) } : {}),
      }));
    data.fields = freezeLabels(data.fields);
    const resolvedWidget = structuredClone(widget);
    if (widget.builtin) {
      resolvedWidget.name = tx(widget.name, {}, locale);
      resolvedWidget.description = tx(widget.description, {}, locale);
    }
    widgets.push(resolvedWidget);
    return [{ id: section.id, title, widget: resolvedWidget, data }];
  });
  return {
    id: crypto.randomUUID(),
    name: template.builtin ? tx(template.name, {}, locale) : template.name,
    templateId,
    templateRevision: template.revision,
    propertyName: template.propertyName,
    generatedAt: new Date().toISOString(),
    sourceAt: template.sourceAt,
    locale,
    parameters,
    sections,
    bindingResults: results,
    definitions: { template, widgets, bindings },
  };
}
export function widgetUsages(
  state: ReportingState,
  id: string,
): { id: string; name: string; kind: "dashboards" | "templates" }[] {
  return [
    ...state.dashboards
      .filter((d) => d.widgets.some((w) => w.widgetId === id))
      .map((d) => ({ id: d.id, name: d.name, kind: "dashboards" as const })),
    ...state.templates
      .filter((t) =>
        t.sections.some((s) => s.kind === "widget" && s.widgetId === id),
      )
      .map((t) => ({ id: t.id, name: t.name, kind: "templates" as const })),
  ];
}
export function bindingUsages(state: ReportingState, id: string): string[] {
  return [
    ...state.dashboards
      .filter((d) => d.widgets.some((w) => w.bindingId === id))
      .map((d) => d.name),
    ...state.templates
      .filter(
        (t) =>
          t.bindingIds.includes(id) ||
          t.sections.some(
            (s) =>
              (s.kind === "widget" && s.bindingId === id) ||
              s.condition?.bindingId === id,
          ),
      )
      .map((t) => t.name),
  ];
}
export function deleteDefinition(
  state: ReportingState,
  kind: "widgets" | "dashboards" | "templates",
  id: string,
): ReportingState {
  if (kind === "widgets" && widgetUsages(state, id).length)
    throw new Error(
      "Detach this Widget from its Dashboards and Report Templates before deleting it.",
    );
  return {
    ...state,
    [kind]: state[kind].filter((item) => item.id !== id),
    deletedBuiltins: [...new Set([...state.deletedBuiltins, `${kind}:${id}`])],
  };
}

export function matrixResult(
  data: QueryResult,
  from: string,
  to: string,
): QueryResult[] {
  const start = new Date(`${from}T00:00:00Z`),
    finish = new Date(`${to}T00:00:00Z`);
  const dates: string[] = [];
  for (let d = start; d <= finish; d = new Date(d.getTime() + 86400000))
    dates.push(d.toISOString().slice(0, 10));
  const weeks: string[][] = [];
  for (const day of dates) {
    if (!weeks.length || new Date(day).getUTCDay() === 1) weeks.push([]);
    weeks.at(-1)!.push(day);
  }
  return weeks.map((days) => {
    const fields: ResultField[] = [
      "unit_id",
      "group_name",
      "classification",
    ].map((name) => ({
      name,
      label:
        name === "unit_id"
          ? "Property Unit"
          : name === "group_name"
            ? "Reporting group"
            : "Classification",
      type: "string",
      format: "number",
    }));
    for (const day of days)
      for (const column of [1, 2])
        fields.push({
          name: `${day}_${column}`,
          label: `${day} · ${column === 1 ? "Property company" : "HCR Rentals"}`,
          type: "number",
          format: "number",
        });
    return {
      ...data,
      columns: fields.map((f) => f.name),
      fields,
      rows: data.rows.map((row) => {
        const result: Row = {
          unit_id: row.unit_id,
          group_name: row.group_name,
          classification: row.classification,
        };
        for (const day of days)
          for (const column of [1, 2])
            result[`${day}_${column}`] =
              day >= "2026-10-12" && day <= "2026-10-18" ? 0 : null;
        for (const service of (row.services ?? []) as Row[]) {
          const day = String(service.service_date).slice(0, 10),
            key = `${day}_${service.source_column}`;
          if (Object.hasOwn(result, key))
            result[key] = service.recorded_quantity as Cell;
        }
        return result;
      }),
    };
  });
}
